import * as React from 'react';
import { useState, useEffect } from 'react';
import { Typography, Paper, Box, Button, Stack, Chip, CircularProgress, Alert } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CasterService from './casterService';
import ReceiverService from '../gnss_receiver/receiverService';

const casterService = new CasterService();
const receiverService = new ReceiverService();

export default function CasterTools() {
  const [casterStatus, setCasterStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restarting, setRestarting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchStatus = () => {
    setLoading(true);
    setError('');

    receiverService.getCasterStatus()
      .then((casterResp) => {
        setCasterStatus(casterResp);
        setLoading(false);
      })
      .catch((error) => {
        setError('Failed to fetch caster status');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchStatus();
    // Refresh status every 10 seconds
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleSync = () => {
    setSyncing(true);
    setError('');
    setSuccess('');
    casterService.rehash()
      .then(() => {
        setSuccess('NTRIP Caster synchronized with database successfully');
        setSyncing(false);
        setTimeout(fetchStatus, 2000);
      })
      .catch((error) => {
        setError('Failed to sync NTRIP Caster with database');
        setSyncing(false);
      });
  };

  const handleRestart = () => {
    setRestarting(true);
    setError('');
    setSuccess('');
    casterService.restart()
      .then(() => {
        setSuccess('NTRIP Caster service restarted successfully');
        setRestarting(false);
        setTimeout(fetchStatus, 2000);
      })
      .catch((error) => {
        setError('Failed to restart NTRIP Caster service');
        setRestarting(false);
      });
  };

  return (
    <React.Fragment>
      <h1 className="page-title"><span className="fw-semi-bold">NTRIP Caster Tools</span></h1>

      <Stack spacing={3} sx={{ maxWidth: 800 }}>
        {/* Global error/success messages */}
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        {/* NTRIP Caster Service Status & Controls */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>NTRIP Caster Service</Typography>

          {loading && !casterStatus ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography>Loading service status...</Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1">Status:</Typography>
                {casterStatus?.running ? (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Running"
                    color="success"
                    size="small"
                  />
                ) : (
                  <Chip
                    icon={<ErrorIcon />}
                    label={casterStatus?.status || 'Stopped'}
                    color="error"
                    size="small"
                  />
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Sync the caster with the database to apply changes made to users, mountpoints, or relays.
              </Typography>

              <Button
                variant="contained"
                startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                onClick={handleSync}
                disabled={syncing}
                color="primary"
              >
                {syncing ? 'Syncing...' : 'Sync Caster'}
              </Button>

              <Typography variant="caption" color="text.secondary">
                Use this function if the NTRIP caster is not picking up changes after database modifications.
              </Typography>

              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Restart the NTRIP caster service for a complete reset if needed.
                </Typography>

                <Button
                  variant="contained"
                  startIcon={restarting ? <CircularProgress size={20} color="inherit" /> : <RestartAltIcon />}
                  onClick={handleRestart}
                  disabled={restarting}
                  color="warning"
                >
                  {restarting ? 'Restarting...' : 'Restart Service'}
                </Button>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Restarting the NTRIP Caster service will temporarily disconnect all NTRIP users.
                  They will need to reconnect after the service restarts.
                </Typography>
              </Box>
            </Stack>
          )}
        </Paper>
      </Stack>
    </React.Fragment>
  );
}
